// ADR 25.9.26q §2/§3 — when the browser starts downloading the wasm.
//
// Until this ADR a build-time `<link rel="preload">` (24.9.26ae) started the
// 642 kB download at HTML parse for every visitor. Lighthouse's lantern charges
// every request that starts before the observed LCP to the simulated LCP,
// whatever its priority, so a signed-out `/signin` — which needs the wasm only
// for the proof-of-work on submit — paid for it with ~2 s of simulated LCP.
//
// So the early start is now decided per visitor, from two synchronous signals:
//
// - **Hinted** (a stored refresh token, or a landing path under `/vocab` or
//   `/s/`, whose public pages render through `gated()` whoever visits): one
//   High-priority preload inserted from the entry (`preloadIfHinted`), and the
//   facade import started at once (`startWasm(true, …)`). A revoked token is a
//   wrong hint, and costs exactly today's behaviour: one early download.
// - **Unhinted**: the facade import waits for window `load`, then the first
//   paint plus idle (`afterLoad` → the injected `whenPainted`, 2 s at most of
//   idle), or the first focus of a credential field (`warmWasm`), whichever
//   comes first, so a visitor who starts typing early does not pay the whole
//   download at submit. ADR 1.10.26p §2 — the paint half: on this SPA `load`
//   fires before the first paint (the render awaits `initI18n`), so `load`
//   alone could start the download before the observed LCP.
//
// **Why not at parse time.** `public/_headers`' `script-src` carries no
// `'unsafe-inline'`, hash or nonce, and `headers.test.ts` pins it (11.9.26c
// AC1); an inline head script reading `localStorage` would widen both it and
// Infra's probe. Starting from the entry costs a hinted visitor one entry
// download of head start, which is the stated price.
//
// Everything is injected — the token read, the document, the window and the
// loader — because no test imports `main.tsx`, and this module imports nothing
// from `./index` or `../auth` so it stays a boot-graph leaf (25.9.26f AC1).

/** The window seam `afterLoad` calls — always as a method on `win`, never destructured. */
export type LoadWindow = Pick<Window, 'addEventListener'>

/** ADR 1.10.26p §2 — `app/firstPaint.ts`'s `afterFirstPaint`, injected so this stays a leaf. */
export type WhenPainted = (fn: () => void) => void

/**
 * `/vocab` exactly or beneath it (never `/vocabulary`), a share link, or
 * `/upgrade` exactly (never `/upgrades`) — ADR 1.10.26c §5: a phone arriving
 * cold from a trial mail has no stored token, and its plan badges need the wasm.
 */
function isPublicWasmRoute(pathname: string): boolean {
  return (
    pathname === '/vocab' ||
    pathname.startsWith('/vocab/') ||
    pathname.startsWith('/s/') ||
    pathname === '/upgrade'
  )
}

/**
 * A throwing read — blocked storage raises `SecurityError` — reads as unhinted.
 * Exported for `main.tsx`'s early exit (ADR 1.10.26p §1), which reads it once.
 */
export function hasToken(readToken: () => string | null): boolean {
  try {
    return readToken() !== null
  } catch {
    return false
  }
}

/** §2 — reads the hint (never throws) and, when it is set, inserts the one preload. Returns the hint. */
export function preloadIfHinted(deps: {
  readToken: () => string | null
  pathname: string
  doc: Pick<Document, 'createElement' | 'head'>
  href: string
}): boolean {
  const hinted = hasToken(deps.readToken) || isPublicWasmRoute(deps.pathname)
  if (!hinted) return false
  // The request mode and credentials of `fetch(wasmUrl)` with no init (`cors`,
  // `same-origin`); any mismatch downloads the file twice. No `fetchpriority`:
  // a hinted visitor needs it soon, so it keeps High.
  // Attributes rather than properties: `as` has no reflecting property in
  // every DOM the tests run under, and the attribute is what the browser reads.
  const link = deps.doc.createElement('link')
  link.setAttribute('rel', 'preload')
  link.setAttribute('as', 'fetch')
  link.setAttribute('type', 'application/wasm')
  link.setAttribute('crossorigin', 'anonymous')
  link.setAttribute('href', deps.href)
  deps.doc.head.appendChild(link)
  return true
}

/**
 * §3 — window `load` (at once if `readyState` is `complete`), then `whenPainted(fn)`.
 * ADR 1.10.26p §2 — the idle step moved into `afterFirstPaint`, so there is one idle, not two.
 */
export function afterLoad(
  win: LoadWindow,
  doc: Pick<Document, 'readyState'>,
  whenPainted: WhenPainted,
  fn: () => void,
): void {
  const painted = () => whenPainted(fn)
  if (doc.readyState === 'complete') painted()
  else win.addEventListener('load', painted, { once: true })
}

// The pending import's once-only trigger. Replaced by each `startWasm`, which
// keeps tests independent; `null` before the first and on the hinted arm.
let trigger: (() => void) | null = null

/** §2/§3 — the facade import: at once when hinted, else on `afterLoad` or the first `warmWasm()`. */
export function startWasm<F>(
  hinted: boolean,
  deps: { doc: Pick<Document, 'readyState'>; win: LoadWindow; whenPainted: WhenPainted; load: () => Promise<F> },
): Promise<F> {
  if (hinted) {
    trigger = null
    return deps.load()
  }
  return new Promise<F>((resolve, reject) => {
    let fired = false
    const fire = () => {
      if (fired) return
      fired = true
      try {
        deps.load().then(resolve, reject)
      } catch (error) {
        reject(error)
      }
    }
    trigger = fire
    afterLoad(deps.win, deps.doc, deps.whenPainted, fire)
  })
}

/** §3 — start the pending import now if it has not started. Idempotent; a no-op before `startWasm`. */
export function warmWasm(): void {
  trigger?.()
}
